import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Expression, StatusIndicator} from '@models/machine/status-indicator';
import {Machine} from '@models/machine/machine';
import {CORRELATIONS} from '@constants/administration/machine/machine-constants';
import {Operators} from '@constants/administration/machine/operators';
import {MachineService} from '@services/administration/machine/machine.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {Signal} from '@models/dashboard/signal';
import {ChartConfig} from '@models/machine/chart-config';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';

@Component({
    selector: 'scitis-admin-machine-status-indicator',
    templateUrl: './status-indicator.component.html',
    styleUrls: ['./status-indicator.component.scss'],
    standalone: false
})
export class StatusIndicatorComponent implements OnInit, OnChanges {

  @Input()
  public signalsArray: Signal[] = [];
  @Input()
  public machine: Machine = new Machine();

  @Input()
  userRole: Role = new Role(new User());

  public possibleCorrelations = CORRELATIONS;
  public operators: string[] = [];
  public selectedCorrelation: string = '';
  public selectedOperator: string = '';
  public selectedSignal: Signal | null = null;
  public expressionValue: string = '';
  private operatorsVariables = Object.keys(Operators);
  private CURRENT_PAGE_NAME = 'MachineConfiguration - Status indicator';

  constructor(public translate: TranslateService,
              private machineService: MachineService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }
    if (changes.machine.currentValue) {
      this.loadStatusIndicatorSettings();
      this.loadOperators();
    }

  }

  ngOnInit(): void {
    this.loadStatusIndicatorSettings();
    this.loadOperators();
  }

  onRemoveExpression(expr: Expression) {
    const index: number = this.machine.chartConfig.statusIndicator.expressions.indexOf(expr, 0);
    this.machine.chartConfig.statusIndicator.expressions.splice(index, 1);
  }

  onAddExpression() {
    const expressionToSave: Expression = new Expression();
    expressionToSave.correlation = this.selectedCorrelation;
    expressionToSave.operator = this.changeOperator(this.selectedOperator);
    if (this.selectedSignal) {
      expressionToSave.signalLabel = this.selectedSignal.label;
      expressionToSave.signal = this.selectedSignal.name;
    }
    expressionToSave.value = this.expressionValue;
    this.machine.chartConfig.statusIndicator.expressions.push(expressionToSave);

    this.deleteInputFields();
  }

  showSaveButton() {
    return (this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole);
  }

  onSaveStatusIndicator() {
    if (!this.machine || !this.machine.id || !this.machine.chartConfig) {
      return;
    }

    this.loadingIndicator.show(this.translate.instant('saving configuration for ') + ' ' + this.machine.name);
    this.machineService.saveStatusIndicatorConfiguration(this.machine.id, this.machine.chartConfig).subscribe(() => {
      let message = this.translate.instant('Machine %machine status indicator configuration saved.');
      message = message.replaceAll('%machine', this.machine.name.toBold());
      this.notification.success(message, this.CURRENT_PAGE_NAME, false);
      this.loadingIndicator.hide();
    }, (error) => {
      this.notification.error(this.translate.instant('Error saving machine status indicator configuration'), error, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
    });
  }

  public getShownOperator(operator) {
    switch (operator) {
      case 'GREATER': {
        return Operators.GREATER;

      }
      case 'LESS': {
        return Operators.LESS;

      }
      case 'EQUAL': {
        return Operators.EQUAL;

      }
    }
    return '';
  }

  private deleteInputFields() {
    this.selectedOperator = '';
    this.selectedSignal = null;
    this.selectedCorrelation = '';
  }

  private loadOperators() {
    this.operators = [];
    this.operatorsVariables.forEach(element => {
      this.operators.push(Operators[element]);
    });
  }

  private loadStatusIndicatorSettings() {
    if (!this.machine) {
      return;
    }

    if (!this.machine.chartConfig) {
      this.machine.chartConfig = new ChartConfig();
    }

    if (!this.machine.chartConfig.statusIndicator) {
      this.machine.chartConfig.statusIndicator = new StatusIndicator();
    }

    if (!this.machine.chartConfig.statusIndicator.expressions) {
      this.machine.chartConfig.statusIndicator.expressions = [];
    }
  }

  private changeOperator(operator: string) {
    switch (operator) {
      case Operators.GREATER: {
        operator = this.operatorsVariables[0];
        break;
      }
      case Operators.LESS: {
        operator = this.operatorsVariables[1];
        break;
      }
      case Operators.EQUAL: {
        operator = this.operatorsVariables[2];
        break;
      }
    }
    return operator;
  }
}
